/**
 * Auth Provider Component
 * ავტორიზაციის პროვაიდერი - გადატანადი ნებისმიერ პროექტში
 */

import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import type { AuthContextType, AuthState, KeycloakConfig, User } from '../types';
import { initKeycloak, initKeycloakOnce, extractUserFromToken, defaultInitOptions, saveTokens, clearTokens, getStoredTokens } from './keycloak';
import { getApiClient } from '../api/apiClient';
import type Keycloak from 'keycloak-js';

const AuthContext = createContext<AuthContextType | null>(null);

// Session storage key for tracking OAuth callback state
const CALLBACK_STATE_KEY = 'keycloak_callback_pending';

/**
 * Check if URL contains Keycloak OAuth callback parameters
 * and save state to sessionStorage so it persists after keycloak clears URL
 */
function checkAndSaveCallbackState(): boolean {
  const params = new URLSearchParams(window.location.search);
  const isCallback = params.has('code') && params.has('session_state');

  if (isCallback) {
    sessionStorage.setItem(CALLBACK_STATE_KEY, 'true');
    return true;
  }

  // Check if we previously detected a callback (before keycloak cleared URL)
  const wasCallback = sessionStorage.getItem(CALLBACK_STATE_KEY) === 'true';
  return wasCallback;
}

/**
 * Clear callback state after authentication completes
 */
function clearCallbackState(): void {
  sessionStorage.removeItem(CALLBACK_STATE_KEY);
}

interface AuthProviderProps {
  children: React.ReactNode;
  config: KeycloakConfig;
  initOptions?: Keycloak.KeycloakInitOptions;
  onAuthenticated?: (user: User) => void;
  onLogout?: () => void;
  LoadingComponent?: React.ReactNode;
}

export function AuthProvider({
  children,
  config,
  initOptions,
  onAuthenticated,
  onLogout,
  LoadingComponent,
}: AuthProviderProps) {
  const [state, setState] = useState<AuthState>({
    isAuthenticated: false,
    isLoading: true,
    user: null,
    token: null,
    error: null,
  });

  const [keycloak, setKeycloak] = useState<Keycloak | null>(null);

  // Track if we came from OAuth callback - captured BEFORE keycloak clears URL
  const [wasCallback] = useState(() => checkAndSaveCallbackState());

  // Initialize Keycloak
  useEffect(() => {
    const kc = initKeycloak(config);
    setKeycloak(kc);

    const init = async () => {
      try {
        // Restore stored tokens so page reload preserves the session
        const storedTokens = getStoredTokens();

        const authenticated = await initKeycloakOnce(kc, {
          ...defaultInitOptions,
          ...initOptions,
          ...storedTokens,
        });

        if (authenticated) {
          // Ensure token is fresh (refresh if expiring within 30s)
          try {
            await kc.updateToken(30);
          } catch {
            // refresh failed — token is expired, clear and treat as unauthenticated
            clearTokens();
            setState({
              isAuthenticated: false,
              isLoading: false,
              user: null,
              token: null,
              error: null,
            });
            clearCallbackState();
            return;
          }

          saveTokens(kc);

          const user = extractUserFromToken(kc);
          setState({
            isAuthenticated: true,
            isLoading: false,
            user,
            token: kc.token || null,
            error: null,
          });

          // Clear callback state on successful auth
          clearCallbackState();

          // Ensure user is created in database by calling /users/me
          try {
            const api = getApiClient();
            await api.get('/users/me');
          } catch (error) {
            console.warn('Failed to sync user with backend:', error);
          }

          if (user && onAuthenticated) {
            onAuthenticated(user);
          }
        } else {
          clearTokens();
          setState({
            isAuthenticated: false,
            isLoading: false,
            user: null,
            token: null,
            error: null,
          });
          // Clear callback state - auth failed or no session
          clearCallbackState();
        }
      } catch (error) {
        console.error('Keycloak init error:', error);
        clearTokens();
        setState({
          isAuthenticated: false,
          isLoading: false,
          user: null,
          token: null,
          error: 'Failed to initialize authentication',
        });
        // Clear callback state on error
        clearCallbackState();
      }
    };

    init();

    // Token refresh
    kc.onTokenExpired = () => {
      kc.updateToken(30)
        .then((refreshed) => {
          if (refreshed) {
            saveTokens(kc);
            setState((prev) => ({
              ...prev,
              token: kc.token || null,
            }));
          }
        })
        .catch(() => {
          console.error('Failed to refresh token');
          clearTokens();
          setState({
            isAuthenticated: false,
            isLoading: false,
            user: null,
            token: null,
            error: 'Session expired',
          });
        });
    };

    // Auth success callback
    kc.onAuthSuccess = () => {
      saveTokens(kc);
      const user = extractUserFromToken(kc);
      setState({
        isAuthenticated: true,
        isLoading: false,
        user,
        token: kc.token || null,
        error: null,
      });
    };

    // Auth error callback
    kc.onAuthError = () => {
      clearTokens();
      setState({
        isAuthenticated: false,
        isLoading: false,
        user: null,
        token: null,
        error: 'Authentication failed',
      });
    };

    // Logout callback
    kc.onAuthLogout = () => {
      clearTokens();
      setState({
        isAuthenticated: false,
        isLoading: false,
        user: null,
        token: null,
        error: null,
      });
      onLogout?.();
    };
  }, [config, initOptions, onAuthenticated, onLogout]);

  const login = useCallback(async (options?: { idpHint?: string; redirectUri?: string }) => {
    if (keycloak) {
      await keycloak.login(options);
    }
  }, [keycloak]);

  const logout = useCallback(async () => {
    if (keycloak) {
      clearTokens();
      await keycloak.logout({ redirectUri: window.location.origin });
    }
  }, [keycloak]);

  const refreshToken = useCallback(async (): Promise<string | null> => {
    if (!keycloak) return null;

    try {
      await keycloak.updateToken(30);
      saveTokens(keycloak);
      return keycloak.token || null;
    } catch {
      clearTokens();
      return null;
    }
  }, [keycloak]);

  const hasRole = useCallback(
    (role: string): boolean => {
      return state.user?.roles.includes(role) ?? false;
    },
    [state.user]
  );

  const hasAnyRole = useCallback(
    (roles: string[]): boolean => {
      return roles.some((role) => state.user?.roles.includes(role));
    },
    [state.user]
  );

  const contextValue = useMemo<AuthContextType>(
    () => ({
      ...state,
      login,
      logout,
      refreshToken,
      hasRole,
      hasAnyRole,
      wasCallback,
    }),
    [state, login, logout, refreshToken, hasRole, hasAnyRole, wasCallback]
  );

  if (state.isLoading) {
    return (
      <>
        {LoadingComponent || (
          <div className="flex items-center justify-center min-h-screen">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
          </div>
        )}
      </>
    );
  }

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * useAuth hook
 */
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
